import {
  computed,
  onMounted,
  onBeforeUnmount,
  isRef,
  ref,
  unref,
  watch,
} from "vue";
import type { Ref } from "vue";

// Adapted from nice collection: https://logaretm.com/blog/my-favorite-5-vuejs-composables

export const useEventListener = (
  target: Ref<EventTarget | null> | EventTarget,
  event: string,
  handler: (e: Event) => void,
  beforeMount?: (t: EventTarget) => void
) => {
  // if its a reactive ref, use a watcher
  if (isRef(target)) {
    watch(target, (value, oldValue) => {
      oldValue?.removeEventListener(event, handler);
      if (beforeMount != undefined && value != undefined) beforeMount(value);
      value?.addEventListener(event, handler);
    });
  } else {
    // otherwise use the mounted hook
    onMounted(() => {
      if (beforeMount != undefined) beforeMount(target);
      target.addEventListener(event, handler);
    });
  }

  // clean it up
  onBeforeUnmount(() => {
    unref(target)?.removeEventListener(event, handler);
  });
};

export const useMedia = (query: Ref<string> | string) => {
  const mediaQuery = window.matchMedia(unref(query));
  const matches = ref(mediaQuery.matches);

  useEventListener(mediaQuery, "change", (e: Event) => {
    const event = e as MediaQueryListEvent;
    matches.value = event.matches;
  });

  return matches;
};

export const useScreenSize = () => {
  const mobile = useMedia("(min-width: 640px)");
  const tablet = useMedia("(min-width: 1024px)");
  const desktop = useMedia("(min-width: 1280px)");

  return computed(() =>
    desktop.value
      ? "desktop"
      : tablet.value
      ? "tablet"
      : mobile.value
      ? "mobile"
      : undefined
  );
};

export function useOnClickOutside(
  element: Ref<HTMLElement | null>,
  callback: () => unknown
) {
  // `mousedown` or `mouseup` is better than `click` here because it doesn't bubble up like `click`
  // if you've used `click` here, the callback will be run immediately.
  useEventListener(window, "mouseup", (e: Event) => {
    const clicked = e.target as HTMLElement;

    // skip if the root element contains the clicked element
    if (element.value?.contains(clicked)) {
      return;
    }

    callback();
  });
}
